import request from '@/Rest';
import { TranslateHealthKitData } from '../translate/healthKitData.translate';
import { HK_DATA_NAME_MAP } from '../utils/HealthKit';

function parseCustomDateFormat(dateInput) {
  if (!dateInput) return null;
  try {
    if (dateInput && typeof dateInput === 'object' && dateInput.seconds !== undefined) {
      const date = new Date(dateInput.seconds * 1000 + dateInput.nanoseconds / 1000000);
      return date.toISOString();
    }

    if (typeof dateInput === 'string') {
      const standardFormat = dateInput.replace(' at ', ', ');
      const parsedDate = new Date(standardFormat);

      if (isNaN(parsedDate.getTime())) {
        return null;
      }

      return parsedDate.toISOString();
    }

    if (dateInput instanceof Date) {
      return dateInput.toISOString();
    }
    return null;
  } catch (error) {
    return null;
  }
}

export const getHealthKitData = async ({ studyId = '', userId = '', withSort = false }) => {
  try {
    let statsRequest = request.GET(`users/${userId}/healthData/${studyId}/stats`);
    let sleepAnalysisRequest = request.GET(`users/${userId}/healthData/${studyId}/sleepAnalysis`);

    const [statsResponse, sleepAnalysisResponse] = await Promise.all([
      statsRequest.Execute(),
      sleepAnalysisRequest.Execute(),
    ]);

    const statsData =
      statsResponse?.docs?.map((data) =>
        TranslateHealthKitData.DomainFromDTO({
          id: data.id,
          ...data.data(),
        })
      ) || [];

    const sleepAnalysisData =
      sleepAnalysisResponse?.docs?.map((data) =>
        TranslateHealthKitData.DomainFromDTO({
          id: data.id,
          ...data.data(),
        })
      ) || [];

    let combinedData = [...statsData, ...sleepAnalysisData];

    if (withSort) {
      combinedData = combinedData.sort((a, b) => {
        const dateA = new Date(a.startDate);
        const dateB = new Date(b.startDate);
        return dateA - dateB;
      });
    }

    return combinedData;
  } catch (error) {
    throw new Error(error.message);
  }
};



export const getAvailableCategories = async ({ studyId = '', userId = '' }) => {
  try {
    let reportQuery = request.GET(`users/${userId}/healthStudyUploadReport`)
      .WHERE(['studyId', '==', studyId])
      .WHERE(['successSamples', '>', 0])
      .ORDER_BY('successSamples', false)
      .LIMIT(100);

    const reportResponse = await reportQuery.Execute();

    const simplifiedNames = [...new Set(
      reportResponse?.docs?.map(doc => doc.data().dataType).filter(Boolean) || []
    )];
    const fullCodes = [];

    simplifiedNames.forEach(name => {
      if(HK_DATA_NAME_MAP[name]){
        fullCodes.push(HK_DATA_NAME_MAP[name]);
      }
    });

    return fullCodes;
  } catch (error) {
    throw new Error(error.message);
  }
};

export const getHealthKitDataByCategory = async ({ studyId = '', userId = '', codeFilter = '' }) => {
  try {
    const reverseMapping = Object.fromEntries(
      Object.entries(HK_DATA_NAME_MAP).map(([key, value]) => [value, key])
    );
    const simplifiedName = reverseMapping[codeFilter];

    if (!simplifiedName) {
      return [];
    }

    let reportQuery = request.GET(`users/${userId}/healthStudyUploadReport`)
      .WHERE(['studyId', '==', studyId])
      .WHERE(['dataType', '==', simplifiedName])
      .LIMIT(1);

    const reportResponse = await reportQuery.Execute();

    if (!reportResponse?.docs?.length) {
      return [];
    }

    const doc = reportResponse.docs[0];
    const data = doc.data();

    const rawDisplayDate = data.lastDataCollectedAt || data.submittedAt;
    const displayDate = parseCustomDateFormat(rawDisplayDate);
    return [{
      id: doc.id,
      code: {
        codeValue: codeFilter,
        coding: [{ code: codeFilter }]
      },
      value: 1,
      unit: '',
      startDate: displayDate,
      endDate: displayDate,
      lastDataCollectedAt: parseCustomDateFormat(data.lastDataCollectedAt),
      submittedAt: parseCustomDateFormat(data.submittedAt),
      displayDate: displayDate
    }];
  } catch (error) {
    throw new Error(error.message);
  }
};

export const getHealthKitDataForStatistics = async ({
  studyId = '',
  userId = '',
  codeFilter = '',
  startDate = '',
  endDate = '',
  collectionType = 'stats',
}) => {
  try {
    let query = request.GET(`users/${userId}/healthData/${studyId}/${collectionType}`)
      .WHERE(['code.coding', 'array-contains', {
        code: codeFilter,
        display: codeFilter,
        system: "com.apple.health"
      }])
     .ORDER_BY('effectivePeriod.start', true);
     
    if (startDate) {
      const startOfDay = new Date(startDate);
      startOfDay.setHours(0, 0, 0, 0);
      const formattedStartDate = startOfDay.toISOString();
      query = query.WHERE(['effectivePeriod.start', '>=', formattedStartDate]);
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      const formattedEndDate = endOfDay.toISOString();
      query = query.WHERE(['effectivePeriod.start', '<=', formattedEndDate]);
    }

    const response = await query.Execute();
    let filteredResults = response?.docs?.filter(
      (doc) => doc.data().code?.coding?.[0]?.code === codeFilter
    ) || [];

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filteredResults = filteredResults.filter((doc) => {
        const docEndDate = new Date(doc.data().effectivePeriod?.end);
        return docEndDate <= endOfDay;
      });
    }

    const adaptedResults = filteredResults.map((doc) =>
      TranslateHealthKitData.DomainFromDTO({
        id: doc.id,
        ...doc.data(),
      })
    );

    return adaptedResults;
  } catch (error) {
    throw new Error(error.message);
  }
};